import { ISortTable, RecurrenceRule } from "../../../utils/types";
import {
    ProviderStatus,
    SessionServiceType,
} from "../../../utils/types/session";
import {
    FlaggedNoteForAuditStatus,
    NoteStatus,
    NoteTypes,
} from "../../notes/types/notes.types";

export enum ClientCardTypes {
    DISCOVER_FINANCIAL = "Discover",
    AMERICAN_EXPRESS = "AmericanExpress",
    MASTERCARD = "MasterCard",
    CAPITAL_ONE = "Capital One",
    CHASE = "Chase",
    VISA = "Visa",
    DINERSCLUB = "DinersClub",
    JCB = "JCB",
}

export enum ProviderCredential {
    PHD = "PhD",
    PSYD = "PsyD",
    LMSW = "LMSW",
    LMHC = "LMHC",
    LCSW = "LCSW",
    MHCLP = "MHC-LP",
    INTERN = "Intern",
    MD = "MD",
    LMSWA = "LMSW-A",
    LMHCA = "LMHC-A",
    LMHCB = "LMHC-B",
    LMHCC = "LMHC-C",
    PSYDA = "PsyD-A",
    PSYDB = "PsyD-B",
    PSYDC = "PsyD-C",
    PHDA = "PhD-A",
    PHDB = "PhD-B",
    PHDC = "PhD-C",
    LCSWA = "LCSW-A",
    LCSWB = "LCSW-B",
    LCSWC = "LCSW-C",
    EXTERN = "Extern",
}

export enum PaymentCardStatus {
    VALIDATED = "validated",
    UNVALIDATED = "unvalidated",
}

export enum RelationshipToPrimaryInsured {
    SELF = "self",
    SPOUSE = "spouse",
    CHILD = "child",
    OTHER = "other",
}

export enum UserSex {
    MALE = "male",
    FEMALE = "female",
    UNKNOWN = "unknown",
}

export enum InsuranceType {
    MEDICARE = "medicare",
    MEDICAID = "medicaid",
    TRICARE = "tricare",
    CHAMPVA = "champva",
    GROUP_HEALTH_PLAN = "group_health_plan",
    FECA_BLK_LUNG = "feca_blk_lung",
    Other = "other",
}

export enum ClientDocumentTypes {
    CONSENT_FORM = "consent form",
    INSURANCE_SCAN = "insurance scan",
    ID_CARD = "id_card",
    APPEAL = "appeal",
    AUTHORIZATION = "authorization",
    CORRESPONDENCE_WITH_PATIENTS = "correspondence_with_patients",
    EXPLANATION_OF_BENEFITS = "explanation_of_benefits",
    OTHER = "other",
}

export enum InsuranceTabSubModals {
    INFO = "info",
    DOCUMENT = "document",
    EDIT = "edit",
    UPLOAD_DOCUMENT = "upload-document",
}

export enum PaymentLinkType {
    OPEN = "open",
    CLOSED = "closed",
}

export enum PaymentLinkStatus {
    ACTIVE = "active",
    USED = "used",
    EXPIRED = "expired",
}
export interface IInsuranceProvidersFilterTypes {
    insurance_provider_name?: string;
    client_id?: string;
}

export interface IClientCreditCardDetails {
    payment_card_id: string;
    merchant_customer_id: string;
    profile_id: string;
    payment_profile_id: string;
    first_name: string;
    last_name: string;
    masked_card_number: string;
    expiration_date: string;
    billing_address: string;
    zip_code: string;
    card_type: ClientCardTypes;
    state: string;
    country: string;
    card_status: PaymentCardStatus;
    created_source: string;
    is_current: boolean;
    card_label: string;
}

export interface INewCreditCardDetails {
    card_label?: string;
    tokenized_payment: {
        opaque_data: {
            dataDescriptor: string;
            dataValue: string;
        };
        billing_info: {
            first_name: string;
            last_name: string;
            billing_address: string;
            state: string;
            zip_code: string;
            country: string;
        };
    };
}

export interface ICardHistory {
    trans_id: string;
    invoice_number: string;
    transaction_status: string;
    submit_time_utc: string;
    first_name: string;
    last_name: string;
    settlement_time_utc: string;
    settle_amount: number;
    payment_description: string;
}

export interface IClientInsuranceProvider {
    insurance_provider_id: string;
    name: string;
    alias: string;
    accumulations_end_date?: string;
    insurance_member_number?: string;
}

export interface ICardFilterTypes {
    page?: number;
    limit?: number;
}

export interface IClientInsuranceAttachment {
    id: string;
    name: string;
    url: string;
    metadata: {
        content_type: string;
    } | null;
    third_party_type: "dropbox" | "google_drive" | null;
}

export interface IClientInsuranceDetails {
    insurance_id: string;
    name: string;
    is_current: boolean;
    employer_name: string;
    non_client_primary_insured_name: string;
    start_date: string;
    end_date: string;
    insurance_member_id: string;
    is_cal_year: boolean;
    last_verified: string | null;
    non_client_primary_insured_dob: string;
    primary_insured_address: string;
    primary_insured_name: string;
    primary_insured_dob: string;
    financial_assistance_beneficiary: string;
    attached_files: IClientInsuranceAttachment[] | null;
    primary_insured_state: string;
    primary_insured_zipcode: string;
    status: ProviderStatus;
    insurance_accepted: boolean;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    primary_insured_city?: string;
    primary_insured_phone_no?: string;
    primary_insured_sex?: UserSex;
    insured_policy_group?: string;
    insurance_type?: InsuranceType;
    accumulations_start_date?: string;
    accumulations_end_date?: string;
    effective_start_date?: string;
    effective_end_date?: string;
    policy_effective_start_date?: string;
    policy_effective_end_date?: string;
}

export interface IClientNewInsuranceDetails {
    author?: string;
    insurance_provider_id: string;
    is_cal_year?: boolean;
    start_date?: string;
    end_date?: string;
    effective_start_date?: string;
    effective_end_date?: string;
    employer_name: string;
    insurance_member_id?: string;
    last_verified?: string;
    non_client_primary_insured_name?: string;
    non_client_primary_insured_dob?: string;
    primary_insured_address?: string;
    primary_insured_state?: string;
    primary_insured_zipcode?: string;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    primary_insured_city?: string;
    primary_insured_phone_no?: string;
    primary_insured_sex?: UserSex;
    primary_insured_dob?: string;
    insured_policy_group?: string;
    insurance_type?: InsuranceType;
}

export interface IClientEditInsuranceDetails {
    insurance_provider_id: string;
    is_cal_year?: boolean;
    start_date?: string;
    end_date?: string;
    employer_name: string;
    insurance_member_id?: string;
    last_verified?: string | null;
    non_client_primary_insured_name?: string | null;
    non_client_primary_insured_dob: string | null;
    primary_insured_address?: string;
    primary_insured_state?: string;
    primary_insured_zipcode?: string;
    status: ProviderStatus;
    insurance_accepted: boolean;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    primary_insured_city?: string;
    primary_insured_phone_no?: string;
    primary_insured_sex?: UserSex;
    primary_insured_dob: string | null;
    insured_policy_group?: string;
    insurance_type?: InsuranceType;
    accumulations_start_date: string | null;
    accumulations_end_date: string | null;
    effective_start_date: string | null;
    effective_end_date: string | null;
    policy_effective_start_date: string | null;
    policy_effective_end_date: string | null;
}
export interface IClientDocumentFilters {
    client_id: string;
    page?: number;
    limit?: number;
    type?: ClientDocumentTypes;
}

export interface IClientDocument {
    document_id: string;
    title: string;
    type: string;
    url: string;
    uploaded_by: {
        user_id: string;
        first_name: string;
        last_name: string;
    };
    uploaded_date: string;
    metadata: {
        content_type: string;
    } | null;
    third_party_type: "dropbox" | "google_drive" | null;
    date_of_service: string | null;
    note_type: NoteTypes | null;
}

export enum ClientDocumentType {
    CONSENT_FORM = "consent form",
    INSURANCE_SCAN = "insurance scan",
    ID_CARD = "id_card",
    APPEAL = "appeal",
    AUTHORIZATION = "authorization",
    CORRESPONDENCE_WITH_PATIENTS = "correspondence_with_patients",
    EXPLANATION_OF_BENEFITS = "explanation_of_benefits",
    OTHER = "other",
}
export interface IClientDocumentsListFilters {
    client_id: string;
    page?: number;
    limit?: number;
    type?: ClientDocumentType;
    document_id?: string;
}

export interface IClientProviderHistory {
    provider_id: string;
    provider_name: string;
    is_current: boolean;
    credential: ProviderCredential;
    service: SessionServiceType;
    no_of_sessions: number;
    first_session_date: string;
    last_seen: string;
    status: ProviderStatus;
    delivery_option?: string;
    session_id: string;
    appointment: {
        session_day: string;
        session_start_hr: number;
        session_start_min: number;
        session_end_hr: number;
        session_end_min: number;
        recurrence_rule: RecurrenceRule;
        session_metadata?: {
            is_paused: boolean;
            is_supervisor_intake_session?: boolean;
        };
    };
}

export interface IClientProviderHistoryListFilters {
    limit: number;
    page: number;
}

export enum ClientMemoCategories {
    INTAKE = "intake",
    BILLING = "billing",
    CLIENT_SERVICE = "client services",
    REPORTS = "reports",
    NOTES = "notes",
    PHONE_NOTES = "phone notes",
}

export enum ClientMemoStatus {
    ACTIVE = "active",
    PAUSED = "paused",
}

export enum ClientMemoType {
    USER = "user",
    SYSTEM = "system",
}

export enum ApplicableFeeSource {
    GENERAL = "general",
    PROVIDER_SPECIFIC = "provider_specific",
}

export interface IClientMemo extends ISortTable {
    memo_id: string;
    category: ClientMemoCategories;
    date_created: string;
    type: "All" | "system" | "user";
    author: {
        first_name: string;
        last_name: string;
        user_id: string;
    } | null;
    description: string;
    date_updated: string;
    updated_by: {
        first_name: string;
        last_name: string;
    };
    memo_status: ClientMemoStatus;
}

export interface INewMemo {
    author?: string;
    category: ClientMemoCategories;
    description: string;
}

export interface IClientMemoListFilterTypes {
    author_name?: string;
    type?: string;
    category?: string[];
    from_date?: null | string;
    to_date?: null | string;
    page?: number;
    limit?: number;
    memo_status?: string;
}

export interface ICollectionsNoteUser {
    user_id: string;
    first_name: string;
    last_name: string;
}

export interface ICollectionsNoteDocument {
    collections_note_document_id: string;
    collections_note_id: string;
    client_id: string;
    url: string;
    uploaded_by: ICollectionsNoteUser | null;
    document_name: string | null;
    metadata: { content_type?: string } | null;
    created_at: string;
}

export interface ICollectionsNote {
    collections_note_id: string;
    client_id: string;
    description: string;
    date_created: string;
    date_updated: string;
    author: ICollectionsNoteUser | null;
    updated_by: ICollectionsNoteUser | null;
    documents: ICollectionsNoteDocument[];
}

export interface INewCollectionsNote {
    description: string;
}

export interface ICollectionsNoteRecord {
    collections_note_id: string;
    client_id: string;
    author: string;
    description: string;
    updated_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface ICollectionsNoteActivity {
    collections_note_activity_id: string;
    collections_note_id: string | null;
    description: string;
    date_created: string;
    author: ICollectionsNoteUser | null;
}

export interface ICollectionsNoteFilterTypes {
    collections_note_id?: string;
    author_name?: string;
    from_date?: null | string;
    to_date?: null | string;
    page?: number;
    limit?: number;
}

export interface IClientNotesListFilterTypes {
    provider_name?: string;
    search_string?: string;
    status?: string[];
    type: string[];
    from_date: null | string;
    to_date: null | string;
    page: number;
    limit: number;
    show_archived?: string;
    sort_attribute?: string;
    sort_order?: string;
    show_flagged_notes?: string;
    show_notes_without_claims?: string;
}

export interface IClientNote {
    note_id: string;
    date_of_service: string;
    note_type: NoteTypes;
    provider: {
        user_id: string;
        last_name: string;
        first_name: string;
    };
    note_url: string;
    status: NoteStatus;
    is_archived: boolean;
    created_at: string;
    flagged_for_audit_status?: FlaggedNoteForAuditStatus;
    flagged_for_audit_notes?: string;
    author?: {
        id: string;
        first_name: string;
        last_name: string;
    };
    external_reference_id?: string;
}

export enum ClientFeeValues {
    MASTERS_FEE = "masters fee",
    DR_FEE = "drs fee",
    INTERN_FEE = "intern fee",
    TESTING_FEE = "testing fee",
    GIFTED_ASSESSMENT_FEE = "gifted assessment fee",
    SHORT_TERM_EVALUATION_DOCUMENTATION_FEE = "short term evaluation documentation fee",
    PSYCH_FEE = "psych fee",
    INN_MASTERS_FEE = "inn masters fee",
    DR_FEE_A = "dr_fee_a",
    DR_FEE_B = "dr_fee_b",
    DR_FEE_C = "dr_fee_c",
    LIC_FEE = "lic_fee",
    LIC_A_FEE = "lic_a_fee",
    LIC_B_FEE = "lic_b_fee",
    LIC_C_FEE = "lic_c_fee",
}

export interface IProviderFee {
    provider_id: string;
    credential: ProviderCredential;
    user_id: string;
    service: SessionServiceType;
    first_name: string;
    last_name: string;
    pronouns: string | null;
    preferred_name: string | null;
    start_date: string;
    session_count: number;
    status: ProviderStatus;
    applicable_fee_value?: string;
    applicable_fee_source?: ApplicableFeeSource;
    applicable_fee_type?: ClientFeeValues;
}

export interface IClientFeeValue {
    provider_id?: string;
    type: ClientFeeValues;
    value: string;
    fee_value_id: string;
    provider?: {
        provider_id: string;
        first_name: string;
        last_name: string;
        credential: ProviderCredential;
    };
}

export interface IClientFee {
    fee_id: string;
    cal_year: number;
    start_date: string;
    end_date: string;
    self_pay: boolean;
    fee_status?: ProviderStatus;
    fee_effective_start_date: string | null;
    fee_effective_end_date: string | null;
    fee_last_verified: string | null;
    accepts_assignment: boolean;
    innded: string | null;
    innco: string | null;
    innoop: string | null;
    inncoins: string | null;
    oonded: string | null;
    oonco: string | null;
    oonoop: string | null;
    ooncoins: string | null;
    ucr: string | null;
    service_type?: string | null;
    created_at: string;
    updated_by: {
        author_id?: string;
        first_name: string;
        last_name?: string;
        email?: string;
        preferred_name?: string;
    };
    updated_by_intake_user: string | null;
    alt_email_sent: boolean;
    updated_at: string;
    reason_for_update?: string;
    fee_values: IClientFeeValue[];
    providers: IProviderFee[];
}

export interface IEditClientFee {
    cal_year: number;
    start_date: string;
    end_date: string;
    self_pay?: boolean;
    accepts_assignment?: boolean;
    alt_email_sent?: boolean;
    reason_for_update: string;
    fee_values: {
        type: string;
        value: string;
        provider_id?: string;
    }[];
    insurance_id?: string;
}

export interface IAddClientFlag {
    content: string;
    color: string;
}

export interface IClientFlag {
    client_flag_id: string;
    content: string;
}

export interface IAddSessionContent {
    provider_id: string;
    service: string;
    session_start_time: string;
    session_end_time: string;
    recurrence_rule: string;
    delivery_option: string;
    start_date: string;
    is_supervisor_intake_session?: boolean;
}

export interface IAddClientSession {
    sessions: IAddSessionContent[];
}

export interface IAddClientSessionResponse {
    created_sessions: string[];
    failed_sessions: {
        data: IAddClientSession;
        error: string;
    }[];
}

export interface IEditSessionContent {
    session_id: string;
    service?: string;
    start_date: string;
    session_start_time: string;
    session_end_time: string;
    recurrence_rule: string;
    delivery_option?: string;
    status?: string;
    is_supervisor_intake_session?: boolean;
}

export interface IEditClientSession {
    sessions: IEditSessionContent[];
}

export interface IEditClientSessionResponse {
    edited_sessions: string[];
    failed_sessions: {
        data: {
            session_id: string;
            service: string;
            session_start_time: string;
            session_end_time: string;
            recurrence_rule: string;
            delivery_option?: string;
        };
        error: string;
    }[];
}

export interface IDeleteClientSession {
    session_ids: string[];
}

export interface IDeleteClientSessionResponse {
    deleted_sessions: string[];
    failed_sessions: {
        data: {
            session_id: string;
            error: string;
        };
    }[];
}

export interface IValidateCardPayload {
    clientId: string;
    paymentCardId: string;
}

export interface ISetPreferredCardPayload {
    clientId: string;
    paymentCardId: string;
    isPreferred: boolean;
}

export interface IDeleteCardsPayload {
    clientId: string;
    card_ids: string[];
}

export interface IDeleteCreditCardsResponse {
    deleted_card_ids: string[];
    undeleted_card_ids: {
        card_id: string;
        reason: string;
    }[];
}

export interface IDeleteInsuranceInformationPayload {
    insurance_ids: string[];
}

export interface IDeleteInsuranceInformationResponse {
    deleted_insurance_ids: string[];
    undeleted_insurance_ids: {
        insurance_id: string;
        reason: string;
    }[];
}

export interface IClientIntakeImportDiagnosisPayload {
    client_ids: string[];
}

export enum ImportOverallStatus {
    SUCCESS = "success",
    FAILURE = "failure",
    WARNING = "warning",
}

export enum ImportCheckStatus {
    PASSED = "passed",
    FAILED = "failed",
    WARNING = "warning",
    SKIPPED = "skipped",
}

export interface IClientIntakeImportDiagnosisResponse {
    results: {
        client_id: string;
        overall_status: ImportOverallStatus;
        summary: string;
        checks: {
            step: string;
            status: ImportCheckStatus;
            message: string;
            suggested_fix?: string;
            details?: string;
        }[];
    }[];
}

export interface IClientInsuranceRecords {
    insurance_id: string;
    insurance_provider_id: string;
    name: string;
    employer_name: string;
    insurance_member_id: string;
    start_date: string;
    end_date: string;
    is_cal_year: boolean;
    status: ProviderStatus;
    insurance_accepted: boolean;
    last_verified: string;
    non_client_primary_insured_name: string;
    non_client_primary_insured_dob: string;
    primary_insured_address: string;
    primary_insured_city: string;
    primary_insured_state: string;
    primary_insured_zipcode: string;
    primary_insured_phone_no: string;
    primary_insured_sex: UserSex;
    primary_insured_dob: string;
    relationship_to_primary_insured: RelationshipToPrimaryInsured;
    insured_policy_group: string;
    insurance_type: InsuranceType;
    financial_assistance_beneficiary: string;
    financial_assistance_start_date: string;
    financial_assistance_end_date: string;
    accumulations_start_date: string;
    accumulations_end_date: string;
    effective_start_date: string;
    effective_end_date: string;
    policy_effective_start_date: string;
    policy_effective_end_date: string;
    state: string;
    attached_files: IClientInsuranceAttachment[];
    fees: IClientFee[];
}

export interface IGeneratePaymentLinkResponse {
    payment_link_ref: string;
    payment_link_url: string;
    coded: boolean;
    link_status: PaymentLinkStatus;
}

export interface IPaymentLinkInvoice {
    invoice_id: string;
    provider_name: string;
    date_of_service: string;
    appointment_type: string | null;
    amount: number;
}

export interface IGeneratePaymentLinkInvoicePayload {
    invoice_id: string;
    coinsurance_amount?: number | null;
    pr100_amount?: number | null;
}

export interface IGeneratePaymentLinkPayload {
    invoices: IGeneratePaymentLinkInvoicePayload[];
    link_type: PaymentLinkType;
    email?: string;
}

export interface IPaymentLinkDetails {
    client_name: string;
    invoices: IPaymentLinkInvoice[];
    total_amount: number;
    link_status: PaymentLinkStatus;
    code_required?: boolean;
    coded?: boolean;
}

export interface CardData {
    cardNumber: string;
    month: string;
    year: string;
    cardCode: string;
    zip?: string;
    fullName?: string;
}

export interface AuthorizeNetSecureData {
    authData: {
        apiLoginID: string;
        clientKey: string;
    };
    cardData: CardData;
}

export interface AcceptJSResponse {
    opaqueData: {
        dataDescriptor: string;
        dataValue: string;
    };
    messages: {
        resultCode: string;
        message: Array<{ code: string; text: string }>;
    };
}

export enum CommunicationDirection {
    INBOUND = "Inbound",
    OUTBOUND = "Outbound",
}

export enum SmsReadStatus {
    READ = "Read",
    UNREAD = "Unread",
}

export enum CallType {
    VOICE = "Voice",
    FAX = "Fax",
}

export type RingCentralDestination =
    | "dialer"
    | "new-sms"
    | "call-history"
    | "sms-history";

export enum CommunicationNoteChannel {
    SMS = "sms",
    CALL = "call",
}

export enum CommunicationNoteOriginSystem {
    MANTLE = "mantle",
    INTAKE = "intake",
}

export interface ICommunicationNote {
    communication_note_id: string;
    client_id: string;
    channel: CommunicationNoteChannel;
    record_id: string;
    body: string;
    origin_system: CommunicationNoteOriginSystem;
    author_email: string;
    author_name: string;
    updated_by_email: string | null;
    updated_by_name: string | null;
    created_at: string;
    updated_at: string;
}

export interface INewCommunicationNote {
    body: string;
}

export interface INewCommunicationNoteRecord extends INewCommunicationNote {
    channel: CommunicationNoteChannel;
    record_id: string;
}

export interface IClientSmsMessage {
    message_id: string;
    direction: CommunicationDirection | null;
    from: string | null;
    to: string | null;
    sent_at: string | null;
    message: string | null;
    delivery_status: string | null;
    read_status: SmsReadStatus | null;
    notes: ICommunicationNote[];
}

export interface IClientCallLog {
    call_id: string;
    direction: CommunicationDirection | null;
    from: string | null;
    to: string | null;
    started_at: string | null;
    duration_seconds: number | null;
    result: string | null;
    action: string | null;
    type: CallType | null;
    notes: ICommunicationNote[];
}

export interface ICommunicationHistoryFilters {
    page?: number;
    limit?: number;
    direction?: string;
    date_from?: string;
    date_to?: string;
}

export interface ISmsHistoryFilters extends ICommunicationHistoryFilters {
    read_status?: string;
}

export interface ICallHistoryFilters extends ICommunicationHistoryFilters {
    call_type?: string;
}

export enum ClientTagKey {
    READY_TO_COMMIT = "ready_to_commit",
    LOW_BARRIER_NOT_BOOKING = "low_barrier_not_booking",
    ON_THE_FENCE = "on_the_fence",
    NOT_YET = "not_yet",
    STOPPED_RESPONDING = "stopped_responding",
    WAITLIST = "waitlist",
    INCOMPLETE_INQUIRY = "incomplete_inquiry",
    INSURANCE_TIER_1 = "insurance_tier_1",
    INSURANCE_TIER_2 = "insurance_tier_2",
    INSURANCE_TIER_3 = "insurance_tier_3",
}

export enum ClientTagKind {
    MANUAL = "manual",
    AUTOMATIC = "automatic",
}

export enum ClientTagOriginSystem {
    MANTLE = "mantle",
    INTAKE = "intake",
}

export enum ClientTagAction {
    ADDED = "added",
    REMOVED = "removed",
}

export interface IClientTag {
    client_tag_id: string;
    client_id: string;
    tag_key: ClientTagKey;
    kind: ClientTagKind;
    origin_system: ClientTagOriginSystem;
    added_by_email: string | null;
    added_by_name: string | null;
    added_at: string | null;
}

export interface IClientTagActivity {
    client_tag_activity_id: string;
    client_tag_id: string | null;
    tag_key: ClientTagKey;
    action: ClientTagAction;
    kind: ClientTagKind;
    origin_system: ClientTagOriginSystem;
    author_email: string;
    author_name: string;
    created_at: string;
}

export interface IClientTagActivityFilterTypes {
    page?: number;
    limit?: number;
}
